import { inject, observer } from "mobx-react";
import React from "react";
import { useTranslation } from "react-i18next";
import { Button, Cell, Text, Toggle } from "src/components";
import { theme } from "src/config";
import { Item, Question } from "src/models";
import { ItemStores, LogStore, SpaceStore } from "src/stores";
import { DataContainer } from "src/stores/dataContainer";
import { IItem, ItemType, QuestionPosition } from "src/types";
import { Card, CardGrid } from "src/views";
import styled from "styled-components";


export interface ITabPanelProps  {
  storage?:    DataContainer<Question>;
  type:        ItemType;
  variant:     "critical" | "priority" | "others";
  toggle?:     boolean;
  itemStores?: ItemStores;
  spaceStore?: SpaceStore;
}

export const TabDataPanel = inject("itemStores", "spaceStore")(observer((props: ITabPanelProps) => {
  const { t, i18n } = useTranslation();
  const { storage, variant, toggle, itemStores, spaceStore, type } = props;

  const [boardFilter, setBoardFilter] = React.useState<boolean>(false);

  const data = storage?.getArray.filter(i => {
      const show = (!toggle || i.position === (boardFilter ? QuestionPosition.Board : QuestionPosition.Before)) &&
      (!spaceStore.projectFilter.length || spaceStore.projectFilterHasProject(i.getProjectID));
      return show;
      }
    )
    || [];

  const borders = {
    'critical': theme.color.criticalBorder,
    'priority': theme.color.priorityBorder,
    'others':   theme.color.primaryBorder,
  }
  const backgrounds = {
    'critical': theme.color.backgroundCritical,
    'priority': theme.color.backgroundPriority,
    'others':   theme.color.backgroundBody,
  }

  return (
    <CardContainer
      borderColor     = {borders[variant]}
      backgroundColor = {backgrounds[variant]}
    >
      <Row spaceBetween>
        <Text size="default" weight="bold">
          {t(variant)} ({data?.length})
        </Text>
        {toggle && (
          <Toggle
            label           = {t("before_meeting")}
            $secondaryLabel = {t("on_board")}
            defaultChecked  = {false}
            onCheckedChange = {setBoardFilter}
          />
        )}
      </Row>
      <CardGrid>
        {data.map((i: Item<IItem>) => (
          <Card
            key     = {i.id}
            data    = {i}
            onClick = {() => itemStores[type].setActive(i)}
            type    = {type}
          />
        ))}
      </CardGrid>
      <ButtonContainer>
        {storage?.hasMore && 
          <Button color="grayDarkest" variant="text" onClick={storage.load}>
            {t("view_more_button")}
          </Button>
        }
      </ButtonContainer>
    </CardContainer>
  )
}));

const CardContainer = styled.div<{backgroundColor: string; borderColor: string;}>`
  border-radius:    4px;
  padding:          1rem 1rem;
  background-color: ${(p) => p.backgroundColor};
  border: 1px solid ${(p) => p.borderColor};
  margin-bottom:    1rem;
  max-height:       40rem;
  overflow:         auto;
`;

const Row = styled(Cell)<{ spaceBetween?: boolean }>`
  display:         flex;
  height:          fit-content;
  flex-direction:  row;
  justify-content: ${(p) => p.spaceBetween && "space-between"};
  list-style-type: none;
  gap:             1.5rem;
`;

const ButtonContainer = styled(Cell)`
  display:         flex;
  flex-direction:  row;
  justify-content: flex-end;
`;